import type { AutomationPeopleSelection } from "@repo/app-types/entities/automation";
import type { TeammateId } from "@repo/app-types/entities/teammate";
import { ComboboxField } from "@repo/ui/combobox/comboboxField";
import type { FC } from "react";
import { useMemo } from "react";
import type { TeammateOption } from "./teammatePicker.js";

/** Sentinel option ids; teammate ids are uuids, so they can never collide. */
const ALL_TEAMMATES_OPTION_ID = "all-teammates";
const ALL_MANAGERS_OPTION_ID = "all-managers";

interface PeopleOption {
  id: string;
  label: string;
}

export interface PeoplePickerProps {
  /** Accessible name for the combobox; the visible field label lives outside. */
  ariaLabel: string;
  teammateOptions: readonly TeammateOption[];
  managerTeammateIds: readonly TeammateId[];
  value: AutomationPeopleSelection;
  onChange: (value: AutomationPeopleSelection) => void;
  /**
   * While the caller's options are still loading, disable the control and show a
   * loading placeholder. The picker only ever renders the options it's handed —
   * it never substitutes a fallback set — so this keeps an in-flight fetch from
   * looking like an empty (or wrong) list.
   */
  isLoading?: boolean;
  /** Renders the picker disabled, e.g. for read-only viewers. */
  isDisabled?: boolean;
}

/**
 * The own-results people picker: one multi-select of every teammate plus the
 * "All teammates" and "All managers" options, all freely combinable. Whether a
 * picked person gets a teammate result or manager treatment is not chosen
 * here — the save path partitions the selection by who manages a group.
 */
export const PeoplePicker: FC<PeoplePickerProps> = (props) => {
  const {
    ariaLabel,
    teammateOptions,
    managerTeammateIds,
    value,
    onChange,
    isLoading,
    isDisabled,
  } = props;

  const options = useMemo<readonly PeopleOption[]>(
    () => [
      { id: ALL_TEAMMATES_OPTION_ID, label: "All teammates" },
      { id: ALL_MANAGERS_OPTION_ID, label: "All managers" },
      ...teammateOptions,
    ],
    [teammateOptions],
  );

  const selectedIds = useMemo(
    () => [
      ...(value.includesAllTeammates ? [ALL_TEAMMATES_OPTION_ID] : []),
      ...(value.includesAllManagers ? [ALL_MANAGERS_OPTION_ID] : []),
      ...value.teammateIds,
    ],
    [value],
  );

  // Individuals a selected "all" option already covers: saving would absorb
  // them into that option, so offering them reads as a click that does
  // nothing. Disabled, they read as covered instead.
  const coveredOptionIds = useMemo(() => {
    const managerIdSet = new Set<string>(managerTeammateIds);
    return teammateOptions
      .filter(({ id }) =>
        managerIdSet.has(id)
          ? value.includesAllManagers
          : value.includesAllTeammates,
      )
      .map(({ id }) => id);
  }, [
    teammateOptions,
    managerTeammateIds,
    value.includesAllManagers,
    value.includesAllTeammates,
  ]);

  return (
    <div className="w-full sm:max-w-md">
      <ComboboxField<PeopleOption, "multiple">
        aria-label={ariaLabel}
        selectionMode="multiple"
        items={options}
        getLabel={(option) => option.label}
        value={selectedIds}
        onChange={(keys) => {
          const nextIds = new Set(keys.map(String));
          onChange({
            includesAllTeammates: nextIds.has(ALL_TEAMMATES_OPTION_ID),
            includesAllManagers: nextIds.has(ALL_MANAGERS_OPTION_ID),
            // Report the ids in options order, so a re-selection can't look
            // like a change to callers that compare the arrays.
            teammateIds: teammateOptions
              .filter(({ id }) => nextIds.has(id))
              .map(({ id }) => id),
          });
        }}
        placeholder={isLoading ? "Loading…" : "Select teammates or managers"}
        chipsLabel={`Selected ${ariaLabel.toLowerCase()}`}
        isDisabled={isLoading || isDisabled}
        disabledKeys={coveredOptionIds}
      />
    </div>
  );
};
